import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useState } from "react";
import { createReducer } from "@reduxjs/toolkit";
import { useDispatch, useSelector, useStore } from "react-redux";
import { copyMusicalConfiguration, musicalConfigurationReducers } from "../store/musicalConfiguration";

const Configurations = createContext(null);
const CurrentConfiguration = createContext(null);
const reducer = createReducer({}, builder => {
  for (const [name, handler] of Object.entries(musicalConfigurationReducers)) {
    builder.addCase(`main/${name}`, handler);
  }
});

// Lives above the router: only six musical fields per trainer survive navigation.
// There is no second application store, chart document, history or saved identity.
export function TrainingConfigurationsProvider({ children }) {
  const [configurations, setConfigurations] = useState({});
  const update = useCallback((name, seed, action) => {
    setConfigurations(current => {
      const next = reducer(current[name] || seed, action);
      return current[name] === next ? current : { ...current, [name]: next };
    });
  }, []);
  const value = useMemo(() => ({ configurations, update }), [configurations, update]);
  return <Configurations.Provider value={value}>{children}</Configurations.Provider>;
}

export function TrainingConfiguration({ name, children }) {
  const store = useStore();
  const registry = useContext(Configurations);
  const [seed] = useState(() => copyMusicalConfiguration(store.getState()));
  // Standalone mounts can use the same isolation without navigation persistence.
  const [local, localDispatch] = useReducer(reducer, seed);
  const update = registry?.update;
  const dispatch = useCallback(action => {
    if (update) update(name, seed, action);
    else localDispatch(action);
  }, [update, name, seed]);
  useEffect(() => {
    if (update) update(name, seed, { type: "training/initialize" });
  }, [update, name, seed]);
  const configuration = registry ? registry.configurations[name] || seed : local;
  const value = useMemo(() => ({ configuration, dispatch }), [configuration, dispatch]);
  return <CurrentConfiguration.Provider value={value}>{children}</CurrentConfiguration.Provider>;
}

// Shared editor controls fall back to the normal Designer Redux behaviour.
// Other UI/practice fields (e.g. tinyScreen) continue to use the original store.
export function useMusicalSelector(selector) {
  const current = useContext(CurrentConfiguration);
  return useSelector(state => selector(current ? { ...state, ...current.configuration } : state));
}

export function useMusicalDispatch() {
  const current = useContext(CurrentConfiguration);
  const dispatch = useDispatch();
  return current?.dispatch || dispatch;
}
